import { ReactNode, JSX, useContext } from "react";

import { useTrackPageview } from "@rewards-web/shared/modules/analytics";

import CaribouIcon from "../../assets/favicon.ico";
import { useDocumentHead } from "./hooks";
import { PageDepthContext } from "./lib";

interface PageProps {
  /**
   * Title to display in the html <title>
   */
  browserTitle: string;

  /**
   * Icon to display in the html <link rel="">
   */
  faviconUrl?: string;

  /**
   * This should not change once deployed to production for a given page.
   */
  analyticsPageName: string;

  /**
   * Component to render for the page
   */
  children: ReactNode;
}

/**
 * Creates a page:
 * - Renders a react router <Route> using the given `path`
 * - Sets the given browser page <title> and favicon (the innermost page wins)
 * - Tracks the pageview with analytics using the given page name identifier
 * - Renders the given element when the URL matches
 */
export function Page({
  browserTitle,
  analyticsPageName,
  children,
  faviconUrl,
}: PageProps): JSX.Element {
  const depth = useContext(PageDepthContext);

  useTrackPageview(analyticsPageName);
  useDocumentHead({
    depth,
    title: browserTitle,
    faviconUrl: faviconUrl ?? CaribouIcon,
  });

  return (
    <PageDepthContext.Provider value={depth + 1}>
      {children}
    </PageDepthContext.Provider>
  );
}
